import React, { useState, useEffect } from 'react';
import { showError, showSuccess, showDeleteConfirm } from '../../utils/sweetAlert';
import Loader from '../shared/Loader';

const CustomOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    page: 1
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const statusLabels = {
    'pending': { label: 'Pendiente', color: '#ffc107', icon: 'fa-clock' },
    'reviewing': { label: 'En Revisión', color: '#17a2b8', icon: 'fa-eye' },
    'approved': { label: 'Aprobado', color: '#28a745', icon: 'fa-check' },
    'in-production': { label: 'En Producción', color: '#6f42c1', icon: 'fa-cog' },
    'completed': { label: 'Completado', color: '#1f8e38', icon: 'fa-check-double' },
    'cancelled': { label: 'Cancelado', color: '#dc3545', icon: 'fa-times' },
    'rejected': { label: 'Rechazado', color: '#6c757d', icon: 'fa-ban' }
  };

  const printPositionLabels = {
    'front': 'Frente',
    'back': 'Espalda',
    'left-sleeve': 'Manga Izquierda',
    'right-sleeve': 'Manga Derecha',
    'left-side': 'Lateral Izquierdo',
    'right-side': 'Lateral Derecho',
    'pocket': 'Bolsillo',
    'full': 'Completo',
    'other': 'Otro'
  };

  const printSizeLabels = {
    'small': 'Pequeño (10x10cm)',
    'medium': 'Mediano (20x20cm)',
    'large': 'Grande (30x30cm)',
    'extra-large': 'Extra Grande (40x40cm)',
    'custom': 'Personalizado'
  };

  const getPrintPositionsLabel = (order) => {
    const positions = order.printPositions?.length ? order.printPositions : [order.printPosition];
    return positions.filter(Boolean).map(position => printPositionLabels[position] || position).join(', ') || 'Sin ubicación';
  };

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [filters.status, filters.page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: filters.page.toString(),
        limit: '20',
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/custom-orders?${params}`);
      const data = await response.json();

      if (data.orders) {
        setOrders(data.orders);
        setPagination(data.pagination);
      }
    } catch (error) {
      showError('Error al cargar pedidos de merchandising');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/custom-orders/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
    fetchOrders();
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/custom-orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Error al actualizar estado');

      showSuccess('Estado actualizado correctamente');
      fetchOrders();
      fetchStats();
      
      if (selectedOrder?._id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      showError('Error al actualizar el estado');
    }
  };

  const handleDelete = async (orderId) => {
    const confirmed = await showDeleteConfirm('¿Eliminar este pedido personalizado?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/custom-orders/${orderId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Error al eliminar');

      showSuccess('Pedido eliminado correctamente');
      fetchOrders();
      fetchStats();
      setSelectedOrder(null);
    } catch (error) {
      showError('Error al eliminar el pedido');
    }
  };

  const downloadImage = (orderId) => {
    window.open(`${process.env.REACT_APP_API_URL}/custom-orders/${orderId}/image`, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="custom-orders-container">
      {/* Stats Cards */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card pending">
            <div className="stat-icon">
              <i className="fa-solid fa-clock"></i>
            </div>
            <div className="stat-info">
              <span className="stat-number">{stats.pendingOrders}</span>
              <span className="stat-label">Pendientes</span>
            </div>
          </div>
          <div className="stat-card total">
            <div className="stat-icon">
              <i className="fa-solid fa-list"></i>
            </div>
            <div className="stat-info">
              <span className="stat-number">{stats.totalOrders}</span>
              <span className="stat-label">Total Pedidos</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Estado:</label>
          <select 
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
          >
            <option value="all">Todos</option>
            {Object.entries(statusLabels).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Buscar por nombre, email o producto..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
          <button type="submit">
            <i className="fa-solid fa-search"></i>
          </button>
        </form>
      </div>

      {/* Orders Table */}
      <div className="orders-table-container">
        {loading ? (
          <Loader />
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-inbox"></i>
            <h4>No hay pedidos de merchandising</h4>
            <p>Los pedidos aparecerán aquí cuando los clientes envíen solicitudes de merchandising.</p>
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Producto</th>
                <th>Personalización</th>
                <th>Cantidad</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order._id} className={selectedOrder?._id === order._id ? 'selected' : ''}>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>
                    <div className="customer-info">
                      <strong>{order.customerName}</strong>
                      <small>{order.customerEmail}</small>
                    </div>
                  </td>
                  <td>
                    <div className="product-info">
                      <strong>{order.productName}</strong>
                      {order.selectedVariant?.size && (
                        <small>{order.selectedVariant.size} - {order.selectedVariant.color}</small>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="product-info">
                      <strong>{getPrintPositionsLabel(order)}</strong>
                      <small>{printSizeLabels[order.printSize] || 'A confirmar'}</small>
                    </div>
                  </td>
                  <td>{order.quantity}</td>
                  <td><strong>${order.totalPrice?.toLocaleString()}</strong></td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: statusLabels[order.status]?.color }}
                    >
                      <i className={`fa-solid ${statusLabels[order.status]?.icon}`}></i>
                      {statusLabels[order.status]?.label}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-view"
                        onClick={() => setSelectedOrder(order)}
                        title="Ver detalles"
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      <button 
                        className="btn-download"
                        onClick={() => downloadImage(order._id)}
                        title="Descargar imagen"
                      >
                        <i className="fa-solid fa-download"></i>
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDelete(order._id)}
                        title="Eliminar"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            disabled={filters.page === 1}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
          <span>Página {filters.page} de {pagination.totalPages}</span>
          <button 
            disabled={!pagination.hasMore}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="order-detail-modal" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedOrder(null)}>
              <i className="fa-solid fa-times"></i>
            </button>

            <div className="modal-header">
              <h3>
                <i className="fa-solid fa-pen-ruler me-2"></i>
                Pedido Personalizado
              </h3>
              <span 
                className="status-badge large"
                style={{ backgroundColor: statusLabels[selectedOrder.status]?.color }}
              >
                <i className={`fa-solid ${statusLabels[selectedOrder.status]?.icon}`}></i>
                {statusLabels[selectedOrder.status]?.label}
              </span>
            </div>

            <div className="modal-body">
              {/* Image Preview */}
              <div className="image-section">
                <h4>Imagen para Estampado</h4>
                <div className="image-preview-container">
                  <img 
                    src={selectedOrder.customImage?.url} 
                    alt="Imagen personalizada"
                    onError={(e) => {
                      e.target.src = '/placeholder-image.svg';
                    }}
                  />
                </div>
                <button 
                  className="download-btn"
                  onClick={() => downloadImage(selectedOrder._id)}
                >
                  <i className="fa-solid fa-download me-2"></i>
                  Descargar Imagen Original
                </button>
              </div>

              {/* Order Details */}
              <div className="details-section">
                <div className="detail-group">
                  <h4>Información del Cliente</h4>
                  <div className="detail-row">
                    <span className="label">Nombre:</span>
                    <span className="value">{selectedOrder.customerName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Email:</span>
                    <span className="value">{selectedOrder.customerEmail}</span>
                  </div>
                  {selectedOrder.customerPhone && (
                    <div className="detail-row">
                      <span className="label">Teléfono:</span>
                      <span className="value">{selectedOrder.customerPhone}</span>
                    </div>
                  )}
                </div>

                <div className="detail-group">
                  <h4>Detalles del Producto</h4>
                  <div className="detail-row">
                    <span className="label">Producto:</span>
                    <span className="value">{selectedOrder.productName}</span>
                  </div>
                  {selectedOrder.selectedVariant?.size && (
                    <div className="detail-row">
                      <span className="label">Variante:</span>
                      <span className="value">
                        {selectedOrder.selectedVariant.size} - {selectedOrder.selectedVariant.color}
                      </span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Cantidad:</span>
                    <span className="value">{selectedOrder.quantity}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Precio Total:</span>
                    <span className="value price">${selectedOrder.totalPrice?.toLocaleString()}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h4>Opciones de Estampado</h4>
                  <div className="detail-row">
                    <span className="label">Ubicaciones:</span>
                    <span className="value">{getPrintPositionsLabel(selectedOrder)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Tamaño:</span>
                    <span className="value">{printSizeLabels[selectedOrder.printSize]}</span>
                  </div>
                </div>

                {selectedOrder.instructions && (
                  <div className="detail-group">
                    <h4>Instrucciones del Cliente</h4>
                    <p className="instructions">{selectedOrder.instructions}</p>
                  </div>
                )}

                <div className="detail-group">
                  <h4>Fechas</h4>
                  <div className="detail-row">
                    <span className="label">Creado:</span>
                    <span className="value">{formatDate(selectedOrder.createdAt)}</span>
                  </div>
                  {selectedOrder.reviewedAt && (
                    <div className="detail-row">
                      <span className="label">Revisado:</span>
                      <span className="value">{formatDate(selectedOrder.reviewedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Update */}
              <div className="status-update-section">
                <h4>Actualizar Estado</h4>
                <div className="status-buttons">
                  {Object.entries(statusLabels).map(([key, { label, color, icon }]) => (
                    <button
                      key={key}
                      className={`status-btn ${selectedOrder.status === key ? 'active' : ''}`}
                      style={{ 
                        '--btn-color': color,
                        backgroundColor: selectedOrder.status === key ? color : 'transparent',
                        borderColor: color,
                        color: selectedOrder.status === key ? '#fff' : color
                      }}
                      onClick={() => handleStatusChange(selectedOrder._id, key)}
                    >
                      <i className={`fa-solid ${icon}`}></i>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomOrders;
